import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import useDrawerTransition from "../../hooks/useDrawerTransition";

const WIDTHS = {
  sm: "w-full sm:w-[32vw] sm:max-w-[480px]",
  md: "w-full sm:w-[46vw] sm:max-w-[720px]",
  lg: "w-full sm:w-[64vw] sm:max-w-[1200px]",
};

/**
 * Sağdan kayarak açılan, ekran yüksekliğini kaplayan panel.
 * Gövde kendi içinde kayar; başlık ve alt bar sabit kalır.
 */
export default function Drawer({
  open,
  onClose,
  title,
  subtitle,
  titleIcon,
  width = "md",
  children,
  footer,
}) {
  const handleEsc = useCallback(
    (e) => {
      if (e.key === "Escape" && onClose) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener("keydown", handleEsc);
    document.body.classList.add("overflow-hidden");
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.classList.remove("overflow-hidden");
    };
  }, [open, handleEsc]);

  const { mounted, entered } = useDrawerTransition(open);

  // Kapanış boyunca içeriği donduruyoruz: çağıran taraf onClose'da state'i
  // sıfırladığında (seçili üye, başlık, form) drawer hâlâ ekranda kayarken
  // "varsayılana dönme" görüntüsü oluşuyordu.
  const frozen = useRef({ children, title, subtitle, titleIcon, footer });
  useEffect(() => {
    if (open) frozen.current = { children, title, subtitle, titleIcon, footer };
  });
  const view = open ? { children, title, subtitle, titleIcon, footer } : frozen.current;

  if (!mounted) return null;

  return createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
          entered ? "opacity-100" : "opacity-0"
        }`}
        style={{ zIndex: 1400 }}
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        data-drawer
        data-entered={entered ? "true" : "false"}
        className={`fixed inset-y-0 right-0 ${WIDTHS[width] || WIDTHS.md} bg-terakkiSurface shadow-2xl flex flex-col dark:bg-[#123B3D] dark:text-white
          transition-transform duration-300 ease-in-out ${entered ? "translate-x-0" : "translate-x-full"}`}
        style={{ zIndex: 1401 }}
      >
        <div className="shrink-0 px-5 py-4 bg-gradient-to-r from-terakkiPrimaryDark to-terakkiPrimary text-white">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {view.titleIcon && (
                <span className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
                  {view.titleIcon}
                </span>
              )}
              <div className="min-w-0">
                {view.title && <h2 className="text-base font-Bold truncate">{view.title}</h2>}
                {view.subtitle && <p className="text-xs text-white/80 truncate">{view.subtitle}</p>}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Kapat"
              className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0 transition-colors"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">{view.children}</div>

        {view.footer && <div className="shrink-0 border-t border-terakkiBorder p-4 dark:border-white/15">{view.footer}</div>}
      </div>
    </>,
    document.body
  );
}
